"uae client";

import React, { useContext, useState, useMemo } from "react";
import DataContext from "../Context/DAtaContext";
import { motion } from "framer-motion";

// icon imports
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FaRegCircleXmark } from "react-icons/fa6";
import { FaChartLine, FaFemale, FaMale } from "react-icons/fa";
import FarmerOverlay from "../Components/FarmerOverlay";

function DeliveryImpact({ users }) {
  const { vegetableBundle, Payment, farmerbundle } = useContext(DataContext);
  const { Villages, Zones } = useContext(DataContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [farmerId, setFarmerId] = useState("");
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("overall");

  const handleFarmer = (id) => {
    setFarmerId(id);
    setOpen(true);
  };

  // Calculate all metrics using useMemo for performance
  const dashboardData = useMemo(() => {
    // Base farmer payments calculation
    const farmerPayments = users
      ?.map((fm) => {
        const userPayments = Payment?.filter(
          (py) => py?.farmer_id === fm?.uuid
        );
        const totalAmount = userPayments?.reduce(
          (sum, payment) => sum + payment.amount,
          0
        );

        return {
          farmer_id: fm?.uuid,
          totalAmount,
          name: fm?.name,
          id: fm?.id,
          gender: fm?.gender,
          village_id: fm?.village_id,
          group: fm?.group_membership_status,
          household_size: fm?.number_of_people_in_house_hold,
          children_under_5: fm?.number_of_children_under_5,
          breastfeeding_children: fm?.number_of_breast_feeding_children,
          field_officer: fm?.creator_id,
          payment_date: userPayments?.[0]?.created_at,
          packageName: fm?.prefered_bundle,
          packageType: fm?.prefered_bundle?.toLowerCase()?.includes("poultry")
            ? "poultry"
            : "crop",
        };
      })
      .filter((farmer) => farmer.totalAmount > 0);

    // Finished farmers (those with completed payments)
    const finishedFarmer = farmerPayments
      ?.map((fm) => {
        const Bundle = farmerbundle?.filter(
          (fb) => fb?.farmer_id === fm?.farmer_id
        );
        const VBundle = Bundle?.map((Bd) =>
          vegetableBundle?.filter((vag) => vag?.id === Bd?.bundle_id)
        );
        const FBundle = VBundle.flat();

        const totalProduct = Bundle?.reduce((total, bundle) => {
          const relatedVegBundles = FBundle?.filter(
            (vgb) => vgb?.id === bundle?.bundle_id
          );
          const bundleTotal = relatedVegBundles?.reduce((sum, vegBundle) => {
            return sum + bundle?.amount * vegBundle?.price;
          }, 0);
          return total + bundleTotal;
        }, 0);

        const difference = totalProduct - fm.totalAmount;

        const village = Villages?.find((v) => v?.id === fm?.village_id);
        const FmZone = Zones?.find((z) => z?.id === village?.zone_id);

        if (difference === 0) {
          return {
            ...fm,
            village: village?.name,
            zone_id: FmZone?.id,
            zone: FmZone?.name,
            amountPaid: totalProduct,
            packageItems: Bundle?.length || 0,
            package_created_at: Bundle?.[0]?.created_at,
            package_delivered_at: Bundle?.[0]?.package_delivered_at,
            deliveryStatus:
              Bundle?.[0]?.package_delivered_at !== null
                ? "delivered"
                : "pending",
          };
        }
        return null;
      })
      .filter(Boolean);

    return { farmerPayments, finishedFarmer };
  }, [users, Payment, farmerbundle, vegetableBundle, Zones, Villages]);

  const { finishedFarmer } = dashboardData;

  const finishedFarmers = finishedFarmer?.filter(
    (farmer) => farmer?.package_delivered_at !== null
  );

  // Village impact data

  const deliveryImpactData = useMemo(() => {
    const villageStats = {};

    finishedFarmers?.forEach((farmer) => {
      const { village, zone, gender, group, household_size, packageName } =
        farmer;

      if (!villageStats[village]) {
        villageStats[village] = {
          village,
          zone,
          total: 0,
          females: 0,
          males: 0,
          inGroups: 0,
          totalHousehold: 0,
          packageCount: {},
          deliveredCount: 0,
        };
      }

      const stats = villageStats[village];
      stats.total += 1;
      stats.totalHousehold += household_size || 0;
      if (gender === "female") stats.females += 1;
      else if (gender === "male") stats.males += 1;
      if (group) stats.inGroups += 1;

      if (packageName) {
        stats.packageCount[packageName] =
          (stats.packageCount[packageName] || 0) + 1;
      }
      stats.deliveredCount += 1; // All in `finishedFarmers` are delivered
    });

    return Object.values(villageStats).map((v) => {
      const femalePercentage = v.total
        ? ((v.females / v.total) * 100).toFixed(0) + "%"
        : "0%";
      const groupPercentage = v.total
        ? ((v.inGroups / v.total) * 100).toFixed(0) + "%"
        : "0%";
      const avgHouseholdSize = v.total
        ? (v.totalHousehold / v.total).toFixed(1)
        : "0.0";

      const topPackage =
        Object.entries(v.packageCount).sort(
          ([, a], [, b]) => b - a
        )?.[0]?.[0] || "N/A";

      const deliveryRate = v.total
        ? ((v.deliveredCount / v.total) * 100).toFixed(0) + "%"
        : "0%";

      return {
        village: v.village,
        zone: v.zone,
        totalFarmers: v.total,
        femaleCount: `${v.females} (${femalePercentage})`,
        maleCount: `${v.males} (${((v.males / v.total) * 100).toFixed(0)}%)`,
        groupPercentage,
        avgHouseholdSize,
        topPackage,
        deliveryRate,
      };
    });
  }, [finishedFarmers]);

  const renderSection = () => {
    switch (activeSection) {
      case "overall":
        return (
          <div className="space-y-8">
            <table className="min-w-full text-sm text-left dark:text-gray-300">
              <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3">Village</th>
                  <th className="px-4 py-3">Zone</th>
                  <th className="px-4 py-3">Total Farmers</th>
                  <th className="hidden px-4 py-3">% Female</th>
                  <th className="px-4 py-3">Female (n)</th>
                  <th className="px-4 py-3">Male (n)</th>
                  <th className="px-4 py-3">% in Groups</th>
                  <th className="px-4 py-3">Avg. Househ</th>
                  <th className="px-4 py-3">Top Package</th>
                  <th className="hidden px-4 py-3">Delivery Rate</th>
                </tr>
              </thead>
              <tbody>
                {deliveryImpactData?.map((row, i) => (
                  <tr key={i} className="border-b dark:border-gray-700">
                    <td className="px-4 py-3">{row.village}</td>
                    <td className="px-4 py-3">{row.zone}</td>
                    <td className="px-4 py-3 text-center">
                      {row.totalFarmers}
                    </td>
                    <td className="hidden px-4 py-3">
                      {row.femaleCount.split(" ")[1]}
                    </td>
                    <td className="px-4 py-3">{row.femaleCount}</td>
                    <td className="px-4 py-3">{row.maleCount}</td>
                    <td className="px-4 py-3">{row.groupPercentage}</td>
                    <td className="px-4 py-3">{row.avgHouseholdSize}</td>
                    <td className="px-4 py-3">{row.topPackage}</td>
                    <td className="hidden px-4 py-3">{row.deliveryRate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      default:
        return null;
    }
  };

  // Search functionality
  const filteredFarmers = finishedFarmers?.filter(
    (farmer) =>
      farmer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farmer?.zone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farmer?.village?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-gray-300">
            Delivery Impact by Village
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive analytics for farmers who have received their packages
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="p-1 mb-8 dark:bg-gray-700">
          <nav className="flex space-x-8">
            {[{ id: "overall", label: "Overall", icon: FaChartLine }].map(
              ({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeSection === id
                      ? "bg-green-600 text-white dark:bg-gray-900/50  "
                      : "text-gray-500 dark:text-gray-300 hover:text-gray-200 hover:bg-gray-800"
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {label}
                </button>
              )
            )}
          </nav>
        </div>

        {/* Main Content */}
        {renderSection()}

        {/* Farmers List Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md dark:bg-slate-900/60">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-300 sm:mb-0">
                Delivered Farmers ({finishedFarmers?.length || 0})
              </h2>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search farmers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-4 rounded-lg outline-none sm:w-64 dark:bg-gray-800 dark:text-gray-200 "
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto h-[400px]">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 ">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 ">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Farmer
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Amount Paid
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Zone/Village
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Package
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-900 uppercase dark:text-gray-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-slate-900/20">
                {filteredFarmers?.map((farmer, index) => (
                  <motion.tr
                    key={farmer.farmer_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10">
                          <div className="flex items-center justify-center w-10 h-10 bg-gray-300 rounded-full">
                            {farmer.gender === "female" ? (
                              <FaFemale className="w-5 h-5 text-yellow-500" />
                            ) : (
                              <FaMale className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-300">
                            {farmer.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ID: {farmer.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                        TZS {farmer?.amountPaid?.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {farmer.packageItems} items
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-200">
                        {farmer.zone}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {farmer.village}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          farmer.packageType === "poultry"
                            ? " text-yellow-800"
                            : " text-green-800"
                        }`}
                      >
                        {farmer.packageName}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {farmer.deliveryStatus === "delivered" ? (
                          <IoMdCheckmarkCircleOutline className="w-5 h-5 mr-2 text-green-500" />
                        ) : (
                          <FaRegCircleXmark className="w-5 h-5 mr-2 text-red-500" />
                        )}
                        <span
                          className={`text-sm ${
                            farmer.deliveryStatus === "delivered"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {farmer.deliveryStatus === "delivered"
                            ? "Delivered"
                            : "Pending"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <button
                        onClick={() => handleFarmer(farmer?.id)}
                        className="text-blue-600 transition-colors hover:text-blue-900"
                      >
                        View Details
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredFarmers?.length === 0 && (
            <div className="py-12 text-center">
              <div className="mb-2 text-lg text-gray-400">No farmers found</div>
              <p className="text-gray-500">
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : "No finished farmers available"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* farmer overlay */}
      <FarmerOverlay FarmerId={farmerId} open={open} setOpen={setOpen} />
    </div>
  );
}

export default DeliveryImpact;
